import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { on } from '@ember/modifier';
import {
  HdsModal,
  HdsTextBody,
  HdsTextDisplay,
  HdsCardContainer,
  HdsIcon,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import { Flex } from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';

// We are required to display the EU private beta modal until the product is generally available.
// This modal will be retired once the product is GA post October 1st, 2025 as part of
// https://hashicorp.atlassian.net/browse/HCPIE-2833
export default class HcpEuPrivateBetaModal extends Component {
  @service privateBeta;

  get showModal() {
    return this.privateBeta.showModal;
  }

  close = () => {
    this.privateBeta.hideModal();
  };

  <template>
    {{#if this.showModal}}
      <HdsModal
        class="hcp-eu-private-beta-modal"
        @onClose={{this.close}}
        @size="large"
        @isDismissDisabled={{false}}
        data-test-hcp-eu-private-beta-modal
        as |M|
      >
        <M.Header @tagline={{t "components.hcp-eu-private-beta-modal.tagline"}}>
          {{t "components.hcp-eu-private-beta-modal.title"}}
        </M.Header>
        <M.Body>
          <HdsTextBody @tag="p" @size="200">
            {{t "components.hcp-eu-private-beta-modal.details"}}
          </HdsTextBody>
          <HdsTextDisplay @size="200" @tag="p" {{boxPadding "2lg 0 md 0"}}>
            {{t "components.hcp-eu-private-beta-modal.description.title"}}
          </HdsTextDisplay>
          <Flex
            @direction="row"
            @gap="md"
            @alignItems="stretch"
            @justifyContent="space-between"
          >
            <HdsCardContainer
              class="hcp-eu-private-beta-modal__card"
              @level="base"
              @hasBorder={{false}}
              {{boxPadding "lg"}}
            >
              <HdsTextBody
                @tag="p"
                @size="100"
                @weight="semibold"
                {{boxPadding "0 0 sm 0"}}
              >{{t
                  "components.hcp-eu-private-beta-modal.description.deployments-in-europe.header"
                }}</HdsTextBody>
              <HdsTextBody @tag="p" @size="100">
                <ul {{boxPadding "0 0 0 md"}} {{boxMargin "0"}}>
                  <li>{{t
                      "components.hcp-eu-private-beta-modal.description.deployments-in-europe.body.eu-deployments"
                    }}</li>
                  <li>{{t
                      "components.hcp-eu-private-beta-modal.description.deployments-in-europe.body.eu-environment"
                    }}</li>
                  <li>
                    {{t
                      "components.hcp-eu-private-beta-modal.description.deployments-in-europe.body.migrating-accounts"
                    }}
                  </li>
                </ul>
              </HdsTextBody>
            </HdsCardContainer>
            <HdsCardContainer
              class="hcp-eu-private-beta-modal__card"
              @level="base"
              @hasBorder={{false}}
              {{boxPadding "lg"}}
            >
              <HdsTextBody
                @tag="p"
                @size="100"
                @weight="semibold"
                {{boxPadding "0 0 sm 0"}}
              >
                {{t
                  "components.hcp-eu-private-beta-modal.description.integrated-platform-experience.header"
                }}
              </HdsTextBody>
              <HdsTextBody @tag="p" @size="100">
                <ul {{boxPadding "0 0 0 md"}} {{boxMargin "0"}}>
                  <li>{{t
                      "components.hcp-eu-private-beta-modal.description.integrated-platform-experience.body.centralized-management"
                    }}</li>
                  <li>{{t
                      "components.hcp-eu-private-beta-modal.description.integrated-platform-experience.body.unified-sso-configuration"
                    }}</li>
                  <li>
                    {{t
                      "components.hcp-eu-private-beta-modal.description.integrated-platform-experience.body.rbac"
                    }}
                  </li>
                </ul>
              </HdsTextBody>
            </HdsCardContainer>
          </Flex>
          <Flex
            @direction="row"
            @gap="xs"
            @alignItems="stretch"
            @justifyContent="space-between"
            {{boxPadding "lg 0 sm 0"}}
          >
            <HdsIcon
              @name="info"
              @size="16"
              @color="highlight"
              @isInline={{true}}
            />
            <HdsTextBody @tag="p" @size="100">{{t
                "components.hcp-eu-private-beta-modal.private-beta-usage"
                htmlSafe=true
              }}</HdsTextBody>
          </Flex>
        </M.Body>
        <M.Footer>
          <HdsButtonSet>
            <HdsButton
              @text={{t "components.hcp-eu-private-beta-modal.get-started"}}
              {{on "click" this.close}}
              data-test-get-started
            />
          </HdsButtonSet>
        </M.Footer>
      </HdsModal>
    {{/if}}
  </template>
}
